import { z } from "zod";

/**
 * Fanout batching configuration.
 *
 * When a step auto-fans-out (an `array<T>` output feeds its single-value `T`
 * input socket), the engine normally spawns one child workflow per item. With a
 * `batch` config the engine instead runs the per-item handler in chunks inside a
 * single Temporal activity (`executeStepBatch`) and rejoins the per-item outputs
 * into an `array<T>` output — cutting the child-workflow-run count from N to
 * `ceil(N / size)`.
 *
 * This is fanout *config*, deliberately separate from the socket-expression
 * language (which only wires data via `output`/`concat`). v1 only applies to a
 * step that is the sole step in its fanned region (a leaf "map"); see the
 * publish-time validation in the workflow definition validators.
 */

/** What to do when a single item's handler throws inside a batch. */
export const BATCH_ITEM_ERROR_POLICIES = ["continue", "fail-fast"] as const;
export type BatchItemErrorPolicy = (typeof BATCH_ITEM_ERROR_POLICIES)[number];

/** Default item-error policy — matches today's all-or-nothing fanout semantics. */
export const DEFAULT_BATCH_ITEM_ERROR_POLICY: BatchItemErrorPolicy =
  "fail-fast";

/** Default within-activity item concurrency — sequential. */
export const DEFAULT_BATCH_CONCURRENCY = 1;

export const WorkflowStepBatchConfigSchema = z.object({
  /**
   * Items processed per `executeStepBatch` activity invocation. A fanout of N
   * items runs `ceil(N / size)` activities; a `size` >= N collapses to one.
   */
  size: z.number().int().positive(),
  /**
   * `fail-fast` (default): the first item that throws fails the run (non-retryable),
   * so Temporal does not re-run the whole batch.
   * `continue`: per-item errors are captured and surfaced as data; the activity
   * never throws on item errors, preserving partial progress.
   */
  onItemError: z.enum(BATCH_ITEM_ERROR_POLICIES).optional(),
  /**
   * Max items processed concurrently within a single activity invocation.
   * Defaults to 1 (sequential). Bounded so a large chunk can't open unbounded
   * parallel work inside one activity. For the Observe DataSource Link trigger
   * this is also the initial backfill's pages-in-parallel width (capped at 16),
   * so concurrency is configured once here rather than split across steps.
   */
  concurrency: z.number().int().positive().optional(),
  /**
   * Observe DataSource Link trigger only: max time (ms) the change-stream flusher
   * holds a partial batch (fewer than `size` changes) before dispatching it,
   * bounding latency when change volume is low. Read by the trigger's flusher,
   * not the batch executor. Defaults to 2000.
   */
  maxWaitMs: z.number().int().min(0).optional(),
  /**
   * When true, after this batched fanout completes the workflow continues-as-new
   * if the fanout source emitted a continuation (its reserved `__continueAsNew`
   * output) — resetting the parent's event history between windows so an
   * unbounded producer (e.g. the Observe DataSource Link trigger paging a whole
   * table, one `size`-row window per run) stays within Temporal's history limits.
   * The producer omits the continuation when exhausted, ending the loop.
   */
  continueAsNew: z.boolean().optional(),
  /**
   * Collapse the resume ledger to ONE StepRun per chunk (page) instead of one
   * per item. Temporal already retries the whole chunk, so for work that is
   * idempotent per item the per-item StepRun rows add no resume safety — only
   * 2N durable rows against a 2-statement write. When true, `executeStepBatch`
   * writes a single page-level StepRun and re-runs the whole page on retry.
   *
   * Applies to BOTH execution paths:
   * - Bulk (`batchHandler`) steps, which are atomic and idempotent by contract.
   * - The per-item loop, where items still execute individually and keep
   *   `onItemError` isolation — only the ledger collapses. This is the bigger
   *   win: per-item steps fan out one item per source row, so a high-cardinality
   *   step like `use_data_mapping` (per-item ONLY for error isolation; its
   *   lookup is a cached pure read) otherwise writes millions of rows.
   *
   * The cost either way is losing per-item resume: a retried chunk re-runs every
   * item. Only set this on a step whose per-item work is idempotent.
   *
   * Undefined falls back to the pipeline-wide default — page-level for the
   * Observe DataSource Link (CDC) pipeline, per-item everywhere else (see
   * `runBatchedFanout`). That implicit default is applied to BULK steps only:
   * a per-item step is not idempotent by contract and may write to external
   * systems, so it never loses per-item resume just by living in a CDC
   * workflow. Per-item steps opt in one at a time by setting this to `true`.
   */
  pageLevelResume: z.boolean().optional(),
});

export type WorkflowStepBatchConfig = z.infer<
  typeof WorkflowStepBatchConfigSchema
>;

/**
 * Persisted-step-config envelope carrying optional batch settings. Mirrors
 * `WorkflowStepConfigExecutionOverrideSchema` — the orchestrator `safeParse`s the
 * raw `WorkflowStep.config` JSONB against this to pull the `batch` field out
 * without caring about the rest of the blob.
 */
export const WorkflowStepConfigBatchSchema = z.object({
  batch: WorkflowStepBatchConfigSchema.optional(),
});
